import { toast } from "react-toastify";

// Remembers which flash class each toast id currently wears. We swap to the
// other one on every repeat because the two classes reference DIFFERENT CSS
// animation names — re-applying the SAME class does not restart a CSS
// animation, but changing the animation name does. The flash classes and their
// keyframes live in src/app/App.css.
const flashClassById = {};

const nextFlashClass = (id) => {
  const next = flashClassById[id] === "toast-flash-a" ? "toast-flash-b" : "toast-flash-a";
  flashClassById[id] = next;
  return next;
};

/**
 * Show a toast that only ever exists once on screen for a given `id`.
 *
 * The first call shows the toast; each repeat while it is still open re-shakes
 * and re-tints the SAME toast instead of stacking a duplicate. Use it for
 * messages a user can re-trigger by repeating the same action (e.g. clicking
 * Save again with a required field still empty).
 *
 * @param {string} id       Stable identity for the message (dedup key).
 * @param {import("react").ReactNode} message  Toast content.
 * @param {object} [options]  react-toastify options; `type` defaults to "error".
 */
export const dedupToast = (id, message, options = {}) => {
  const { type = "error", ...rest } = options;

  if (toast.isActive(id)) {
    // The flash class stays on the toast after the 1.1s animation ends — this is
    // intentional and inert: App.css uses no `animation-fill-mode: forwards`, so
    // nothing is frozen on screen, and react-toastify drops the class along with
    // the toast when it closes. We deliberately do NOT clear it on a timer: a
    // pending clear would race the next repeat and cancel its restart mid-shake.
    toast.update(id, { render: message, type, className: nextFlashClass(id), ...rest });
  } else {
    flashClassById[id] = null; // reset so the first repeat starts on toast-flash-a
    toast(message, { toastId: id, type, ...rest });
  }
};

/** Convenience wrapper for required-field validation (error toast). */
export const notifyRequired = (id, message) => dedupToast(id, message, { type: "error" });
